import { useEffect, useRef, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import { useCountry } from "../context/CountryContext";

export const Suggest = ({ items, active, onPick, render, testid }) => (
  <ul data-testid={testid} className="absolute left-0 right-0 top-full z-30 mt-1.5 max-h-72 overflow-y-auto rounded-2xl border bg-white p-1.5 text-foreground shadow-[0_20px_40px_-20px_rgba(27,16,51,0.35)] fade-in">
    {items.map((it, i) => (
      <li key={i} onMouseDown={(e) => { e.preventDefault(); onPick(it); }} data-testid={`${testid}-item-${i}`}
        className={`flex cursor-pointer items-center gap-2 rounded-xl px-3 py-2.5 text-sm transition-colors ${i === active ? "bg-accent text-primary" : "hover:bg-muted"}`}>
        {render(it)}
      </li>
    ))}
  </ul>
);

export const useListNav = (items, onPick) => {
  const [active, setActive] = useState(-1);
  useEffect(() => setActive(-1), [items]);
  const onKeyDown = (e) => {
    if (!items.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % items.length); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a <= 0 ? items.length - 1 : a - 1)); }
    if (e.key === "Enter" && active >= 0) { e.preventDefault(); onPick(items[active]); }
  };
  return { active, onKeyDown };
};

export const AddressAutocomplete = ({ value, onChange, onSelect, className = "", testid = "address-input", ...rest }) => {
  const { code } = useCountry();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const skip = useRef(false);
  useEffect(() => {
    if (skip.current) { skip.current = false; return; }
    if ((value || "").trim().length < 3) { setItems([]); return; }
    setBusy(true);
    const t = setTimeout(() => api.get("/geo/suggest", { params: { q: value, country: code } })
      .then((r) => { setItems(r.data); setOpen(true); }).catch(() => setItems([])).finally(() => setBusy(false)), 450);
    return () => { clearTimeout(t); setBusy(false); };
  }, [value, code]);
  const pick = (it) => { skip.current = true; onChange(it.label); setOpen(false); setItems([]); onSelect?.(it); };
  const nav = useListNav(items, pick);
  return (
    <div className="relative flex-1">
      <MapPin className="absolute left-4 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-primary" />
      <Input {...rest} data-testid={testid} value={value} autoComplete="off" onKeyDown={nav.onKeyDown}
        onChange={(e) => onChange(e.target.value)} onFocus={() => items.length && setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 120)}
        className={`pl-10 ${className}`} />
      {busy && <Loader2 className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />}
      {open && items.length > 0 && (
        <Suggest items={items} active={nav.active} onPick={pick} testid={`${testid}-suggestions`}
          render={(it) => <><MapPin className="h-4 w-4 shrink-0 text-primary" /><span className="line-clamp-2">{it.label}</span></>} />
      )}
    </div>
  );
};
